import React from "react";
import { Link } from "react-router-dom";
import GitHubStarsLink from "../components/GitHubStarsLink";

const contactCards = [
  {
    id: "feedback",
    overline: "[001] Product feedback",
    title: "Tell us what feels sharp, broken, or missing.",
    body: "If Killswitch gave you a bad call, a great call, or a weird experience, we want the real version.",
    action: "Email feedback",
    href: "mailto:admin@softwired.in?subject=Killswitch%20feedback",
  },
  {
    id: "support",
    overline: "[002] Help",
    title: "Need support with your account or workflow?",
    body: "Login issues, share-link problems, confusing states, or check-in bugs. Send us the details and we will dig in.",
    action: "Email support",
    href: "mailto:admin@softwired.in?subject=Killswitch%20support",
  },
  {
    id: "partner",
    overline: "[003] Softwired",
    title: "Want to work with the team behind it?",
    body: "Killswitch is built by Softwired. For partnerships, product work, or direct contact, reach out below.",
    action: "Visit Softwired",
    href: "https://softwired.in",
  },
];

export default function ContactFeedback() {
  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <nav className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-brand">
            <div className="w-6 h-6 bg-zinc-950 rounded-sm flex items-center justify-center">
              <span className="text-white font-mono text-[10px] font-bold">KS</span>
            </div>
            <div className="leading-none">
              <div className="font-display font-semibold text-lg tracking-tight">Killswitch</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">by Softwired</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <GitHubStarsLink
              className="hidden lg:flex items-center gap-2 text-[15px] font-medium tracking-tight text-zinc-600 hover:text-zinc-950 transition-colors"
            />
            <Link to="/" className="btn btn-ghost" data-testid="contact-home-link">Home</Link>
            <a href="https://softwired.in" target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="contact-softwired-link">
              Softwired
            </a>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 dot-bg opacity-50 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24 relative">
          <div className="max-w-4xl">
            <div className="overline mb-6">[Contact] Softwired x Killswitch</div>
            <h1 className="font-display text-[42px] sm:text-6xl tracking-tighter font-semibold leading-[0.95]">
              Feedback that stings a little
              <span className="font-serif italic text-zinc-500"> is usually the useful kind.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
              Killswitch is built by Softwired. If something feels off, helpful, confusing, or unexpectedly honest, send it to us directly.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 grid md:grid-cols-3 gap-6 stagger">
          {contactCards.map((card) => (
            <div key={card.id} className="card p-6 flex flex-col" data-testid={`contact-card-${card.id}`}>
              <div className="overline mb-4">{card.overline}</div>
              <h2 className="font-display text-2xl tracking-tight font-medium mb-3">{card.title}</h2>
              <p className="text-sm text-zinc-600 leading-relaxed flex-1">{card.body}</p>
              <a
                href={card.href}
                target={card.href.startsWith("http") ? "_blank" : undefined}
                rel={card.href.startsWith("http") ? "noreferrer" : undefined}
                className="btn btn-secondary mt-6 w-fit"
                data-testid={`contact-action-${card.id}`}
              >
                {card.action}
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">[Direct] Reach us</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium leading-tight">
              Keep it simple.
              <span className="font-serif italic text-zinc-500"> Send the truth.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            <div className="card p-6">
              <div className="overline mb-3">Email</div>
              <a href="mailto:admin@softwired.in" className="font-display text-2xl tracking-tight font-medium break-all text-zinc-950">
                admin@softwired.in
              </a>
              <p className="text-sm text-zinc-600 mt-3">Best for support, product feedback, bugs, and founder stories.</p>
            </div>
            <div className="card p-6">
              <div className="overline mb-3">Website</div>
              <a href="https://softwired.in" target="_blank" rel="noreferrer" className="font-display text-2xl tracking-tight font-medium text-zinc-950">
                softwired.in
              </a>
              <p className="text-sm text-zinc-600 mt-3">The team behind Killswitch, product work, and direct contact.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-500 font-mono uppercase tracking-[0.15em]">
          <span>Killswitch by Softwired © {new Date().getFullYear()}</span>
          <span>softwired.in</span>
          <span>admin@softwired.in</span>
        </div>
      </footer>
    </div>
  );
}
